import { Injectable } from '@angular/core';
import { EnvironmentService } from '@angular-monorepo/environment';
import { HttpClient } from '@angular/common/http';
import { PayinMetricBody } from './payin-metric-body.interface';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PayinMetricService {
  tokenCr: string | null = null;
  tokenExpirationTimer: number | null = null;
  operationId!: string;

  constructor(
    private environmentService: EnvironmentService,
    private http: HttpClient,
  ) { }

  sendEvent(body: PayinMetricBody, action: 'open' | 'selection') {
    if (!this.tokenCr || !this.operationId) {
      return;
    }

    body = {
      ...body,
      operationId: this.operationId,
      timestamp: this.getTimestamp()
    }

    const url = `${this.environmentService?.env?.apiConfig?.payinMetricExperienceUrl}/checkout/operation/${action}`;
    const headers = { token: this.tokenCr };

    this.http.post<void>(url, body, { headers }).subscribe({
      next: () => {
        console.log('sendEvent success');
      },
      error: (error) => {
        console.log('sendEvent error', error);
      }
    } );
  }

  sendMultipleEvents(metrics: PayinMetricBody[]) {
    if (!this.tokenCr || !this.operationId || !metrics?.length) {
      return;
    }

    metrics.forEach(metric => {
      metric.operationId = this.operationId;
      metric.timestamp = this.getTimestamp();
    });

    const url = `${this.environmentService?.env?.apiConfig?.payinMetricExperienceUrl}/metrics/multiple`;
    const headers = { token: this.tokenCr };

    this.http.post<void>(url, { metrics }, { headers }).subscribe({
      next: () => {
        console.log('sendMultipleEvents success');
      },
      error: (error) => {
        console.log('sendMultipleEvents error', error);
      }
    } );
  }

  private refreshToken(body: {operationId: string, oldToken: string}): Observable<{tokenCr: string}> {
    return this.http.post<{tokenCr: string}>(`${this.environmentService?.env?.apiConfig?.payinMetricExperienceUrl}/token/renew`, body);
  }

  getDeviceInfo() {
    const ua = navigator.userAgent;
    let deviceType = 'Unknown';
    let browser = 'Unknown';

    // Detección del tipo de dispositivo
    if (/iPhone/i.test(ua)) {
      deviceType = 'iPhone';
    } else if (/Android/i.test(ua)) {
      deviceType = 'Android';
    } else if (/Windows|Mac|Linux/i.test(ua)) {
      deviceType = 'Desktop';
    }

    // Detección del navegador
    if (/Edg/i.test(ua)) {
      browser = 'Edge';
    } else if (/Chrome/i.test(ua) && !/Chromium/i.test(ua)) {
      browser = 'Chrome';
    } else if (/CriOS/i.test(ua)) {
      // Chrome en iOS
      browser = 'Chrome';
    } else if (/Firefox/i.test(ua) || /FxiOS/i.test(ua)) {
      browser = 'Firefox';
    } else if (/Safari/i.test(ua) && !/Chrome/i.test(ua) && !/CriOS/i.test(ua)) {
      browser = 'Safari';
    } else if (/OPR/i.test(ua) || /Opera/i.test(ua)) {
      browser = 'Opera';
    } else if (/Chromium/i.test(ua)) {
      browser = 'Chromium';
    }

    return {
      deviceType,
      browser
    };
  }

  private getTimestamp() {
    const now = new Date();
    return now.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    }) + ' ' + now.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'UTC',
    });
  }

  initTokenExpirationTimer(): void {
    this.clearTokenExpirationTimer();

    if (!this.tokenCr) {
      return;
    }

    if (!this.isValidJwtFormat(this.tokenCr)) {
      this.tokenCr = null;
      console.error('Token no tiene formato JWT válido');
      return;
    }

    try {
      const payload = JSON.parse(atob(this.tokenCr.split('.')[1]));
      const expiration = payload.exp;

      if (!expiration || typeof expiration !== 'number') {
        return;
      }

      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiration = expiration - currentTime;
      const fiveMinutesInSeconds = 5 * 60;

      if (timeUntilExpiration > fiveMinutesInSeconds) {
        const timerDelay = (timeUntilExpiration - fiveMinutesInSeconds) * 1000;

        this.tokenExpirationTimer = setTimeout(() => {
          const body = {
            operationId: this.operationId,
            oldToken: this.tokenCr!
          };
          this.refreshToken(body).subscribe({
            next: (response) => {
              const newToken = response.tokenCr || null;
              if (newToken !== this.tokenCr) {
                this.tokenCr = newToken;
                this.initTokenExpirationTimer();
                console.log('Token renovado');
              }
            },
            error: (error) => {
              this.tokenCr = null;
              console.error('Error al renovar token:', error);
            }
          });
        }, timerDelay) as unknown as number;
      }
    } catch (error) {
      this.tokenCr = null;
      console.error('Error al decodificar el token:', error);
    }
  }

  clearTokenExpirationTimer(): void {
    if (this.tokenExpirationTimer) {
      clearTimeout(this.tokenExpirationTimer);
      this.tokenExpirationTimer = null;
    }
  }

  private isValidJwtFormat(token: unknown): boolean {
    if (!token || typeof token !== 'string') {
      return false;
    }

    const parts = token.split('.');
    return parts.length === 3 && parts.every(part => part.length > 0);
  }
}
